import React, { useState, useMemo, useEffect, useRef, useContext } from 'react';
import { Button, Stack, Typography, Paper, Modal, Tooltip, Grid, Box, TextField, IconButton, useMediaQuery, useTheme } from '@mui/material';
import Delete from '@mui/icons-material/Delete';
import { CanvasContext } from "../contexts/CanvasContext";
import Draggable from 'react-draggable';
import InfoIcon from '@mui/icons-material/Info';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import LightbulbOutlined from '@mui/icons-material/LightbulbOutlined';
import LightbulbTwoTone from '@mui/icons-material/LightbulbTwoTone';

import QuickActions from './QuickActions';
import PromptDock from './PromptDock';
import CloseIcon from '@mui/icons-material/Close';
import { AlternateEmail } from '@mui/icons-material';
import Segment from './Segment';
import '../styles/timeline.css';
import '../styles/segment-flair.css';


const NarrativeTimeline = ({ onSegmentDelete }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { animationBlocks, animationScript, setAnimationScript, concept,
        layers, selectedSegments, setSelectedSegments, setLoading, fetchData, setLayers, setConcept, setGroups,
        setSuggestionOne, setSuggestionTwo,
        selectedAnimation, playAnimation, API_URL, setOverlayMessage, llm, layoutType,
        dragAction, elementOffsets, setDragAction, initiateDragDrop, generateEdit,
        highlightCanvasElements,

    } = useContext(CanvasContext);

    const tabStyles = {
        margin: "0.2em",
        height: isMobile ? "auto" : "100%",
        color: "#5B244C",
        borderColor: "#5B244C",
        backgroundColor: "#F3E7F1",
        padding: isMobile ? "0.3em 0.6em" : "0.5em 1em",
        fontSize: isMobile ? "0.75rem" : "0.875rem",
        whiteSpace: "nowrap",
        minWidth: isMobile ? "auto" : "initial",
        "&:hover": {
            backgroundColor: "#BD7CB0",
            borderColor: "#5B244C",
            color: "#5B244C"
        },
    };

    const tracks = {
        Primary: [],
        Secondary: [],
        Text: [],
        Background: [],
    };

    const [initialStartTime, setInitialStartTime] = useState(null);
    const [draggedSegment, setDraggedSegment] = useState(null);
    const draggedSegmentRef = useRef(null);

    const [isDragging, setIsDragging] = useState(false);
    const [selectedSegment, setSelectedSegment] = useState(null);
    const [expandedTrack, setExpandedTrack] = useState(null);

    const timelineRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const [isRefAttached, setIsRefAttached] = useState(false);

    const [blockIdentifier, setBlockIdentifier] = useState(null);
    const [time, setTime] = useState(null);
    const [elementsInvolvedByRole, setElementsInvolvedByRole] = useState({});
    const [anchorEl, setAnchorEl] = useState(null);

    // Edit mode states
    const [editingSegment, setEditingSegment] = useState(null);
    const [editText, setEditText] = useState('');

    const handleDragDropRequest = async (segment, blockIdentifier, time) => {

        const userId = localStorage.getItem('userId');

        setLoading(true);

        // Add your synchronization logic here
        let query = `Can you edit the animation on this is connected to these elements ${JSON.stringify(segment.targets)}?
        Make sure the animation event targeted starts at this time: ${time}`;

        generateEdit(query);

    };

    useEffect(() => {
        if (timelineRef.current) {
            setIsRefAttached(true);
        }
    }, [timelineRef.current]);

    useEffect(() => {
        const updateContainerWidth = () => {
            if (timelineRef.current) {
                setContainerWidth(timelineRef.current.getBoundingClientRect().width);
            }
        };

        updateContainerWidth();
        window.addEventListener('resize', updateContainerWidth);

        return () => {
            window.removeEventListener('resize', updateContainerWidth);
        };
    }, []);

    // Highlight canvas elements when segments are selected
    useEffect(() => {
        if (highlightCanvasElements) {
            highlightCanvasElements(selectedSegments);
        }
    }, [selectedSegments, highlightCanvasElements]);


    const Ruler = ({ totalDuration, interval }) => {
        const ticks = [];
        for (let i = 0; i <= totalDuration; i += interval) {
            ticks.push(i);
        }

        // Responsive interval for smaller screens
        const displayInterval = isMobile ? 2000 : interval;
        const visibleTicks = ticks.filter(tick => tick % displayInterval === 0);

        return (
            <div className="ruler">
                {visibleTicks.map(tick => (
                    <div
                        key={tick}
                        className="ruler-tick"
                        style={{
                            left: `${(tick / totalDuration) * 100}%`,
                        }}
                    >
                        <Typography variant={isMobile ? "caption" : "body2"}>
                            {tick}s
                        </Typography>
                    </div>
                ))}
            </div>
        );
    };


    const roleColors = {
        primary: '#F1FBFE',
        secondary: '#F5FAE8',
        text: '#FFF8F3',
        background: '#FDF6FA',
    };


    const roleActiveColors = {
        primary: '#00A9E0',
        secondary: '#92C911',
        text: '#E87722',
        background: '#CE0F69',
    };

    const getColorByRole = (role, isActive = false) => {
        if (isActive) {
            return roleActiveColors[role] || '#007bff';
        }
        return roleColors[role] || '#007bff';
    };

    const totalDuration = useMemo(() => {
        let maxEndTime = 0;
        console.log("elementOffsets", elementOffsets);

        if (elementOffsets) {
            Object.keys(elementOffsets).forEach((key) => {
                elementOffsets[key].appearances.forEach((appearance) => {
                    if (appearance.endTime > maxEndTime) {
                        maxEndTime = appearance.endTime;
                    }
                });
            });
        } else {
            console.error('elementOffsets is null or undefined');
        }

        return maxEndTime;
    }, [elementOffsets]);



    const generateIdeasForBlock = async (segment) => {
        try {
            const response = await fetch(`/generate_suggestions_for_block`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ layers, layout_type: layoutType, filename: selectedAnimation, concept, animation_event: segment.animation_event }),
            });
            const data = await response.json();
            setSuggestionOne(data["suggestions"][0]);
            setSuggestionTwo(data["suggestions"][1]);

        } catch (error) {

            // alert("Error");
        }

    }

    const handleMultipleSegmentClick = (segment, trackType) => {
        if (expandedTrack !== trackType) {
            setExpandedTrack(trackType);
        }

        setSelectedSegments((prevSelectedSegments) => {
            const isSelected = prevSelectedSegments.some(s => s.animation_event === segment.animation_event);
            if (isSelected) {
                const newSelectedSegments = prevSelectedSegments.filter((s) => s.animation_event !== segment.animation_event);
                return newSelectedSegments;
            } else {
                return [...prevSelectedSegments, segment];
            }
        });
    };

   

    // Edit mode handlers
    const handleEditSegment = (segment) => {
        setEditingSegment(segment.animation_event);
        setEditText(segment.animation_event);
    };

    const handleSaveEdit = (segment) => {
        // For now, just show an alert - behavior will be implemented later
        alert(`Would send edit request for segment: "${segment.animation_event}" -> "${editText}"`);
        setEditingSegment(null);
        setEditText('');
    };

    const handleCancelEdit = () => {
        setEditingSegment(null);
        setEditText('');
    };

    // Handler for segment deletion
    const handleDeleteSegment = (segment) => {
        // If onSegmentDelete prop is provided, use it
        if (onSegmentDelete) {
            onSegmentDelete(segment);
        } else {
            // Otherwise, just remove from selectedSegments
            setSelectedSegments(prevSelectedSegments => 
                prevSelectedSegments.filter(s => s.animation_event !== segment.animation_event)
            );
            
            // Show a message to the user
            alert(`Segment "${segment.animation_event}" would be deleted. This is a placeholder for actual deletion logic.`);
        }
    };

    // Handler for flair actions
    const handleFlairAction = (action, segmentType, segment) => {
        // Determine what edit to make based on the action
        let query = '';
        
        switch (action) {
            case 'speed-up':
                query = `Make the ${segmentType} animation faster`;
                break;
            case 'slow-down':
                query = `Make the ${segmentType} animation slower`;
                break;
            case 'delay-start':
                query = `Delay the start of the ${segmentType} animation`;
                break;
            case 'stagger':
                query = `Add staggered timing to the ${segmentType} animation`;
                break;
            case 'synchronize':
                query = `Synchronize the ${segmentType} animation with other elements`;
                break;
            case 'type-faster':
                query = `Make the ${segmentType} typing animation faster`;
                break;
            case 'show-all':
                query = `Show all text at once instead of typing for ${segmentType}`;
                break;
            case 'add-bounce':
                query = `Add a bounce effect to the ${segmentType} animation`;
                break;
            case 'hold-longer':
                query = `Hold the ${segmentType} animation longer before finishing`;
                break;
            default:
                query = `Modify the ${segmentType} animation: ${action}`;
        }
        
        // Use the existing generateEdit function
        generateEdit(query);
    };



    if (animationBlocks && Object.keys(animationBlocks).length > 0) {
        Object.entries(animationBlocks).forEach(([trackType, segments]) => {
            segments.forEach((segment, index) => {
                const isSelected = selectedSegments.some(s => s.animation_event === segment.animation_event);
                const segmentClass = isSelected ? 'segment-active' : 'segment';
                segment.color = getColorByRole(segment.target_type.toLowerCase(), isSelected);


                const segmentElement = (
                    <Draggable
                        key={`segment-${index}`}
                        axis="x"
                        bounds="parent"
                        onStart={(e, data) => {
                            setIsDragging(true);
                            if (initialStartTime === null) {
                                setInitialStartTime(segment.start_time);
                            }
                            setDraggedSegment(segment);
                            // Don't override selectedSegments on drag start - preserve multi-selection
                            draggedSegmentRef.current = e.target;
                        }}
                        onStop={(e, data) => {
                            setIsDragging(false);
                            if (draggedSegmentRef.current) {
                                const rect = draggedSegmentRef.current.getBoundingClientRect();
                                let percentageLeft = (rect.left / containerWidth) * 100;
                                percentageLeft -= 4.17;

                                const time = (percentageLeft / 100) * totalDuration;

                                let elementsInvolvedByRole = {};

                                if (Array.isArray(draggedSegment.originalSegments)) {
                                    draggedSegment.originalSegments.forEach((originalSegment) => {
                                        if (!elementsInvolvedByRole[draggedSegment.groupRole]) {
                                            elementsInvolvedByRole[draggedSegment.groupRole] = [];
                                        }
                                        elementsInvolvedByRole[draggedSegment.groupRole].push({
                                            "block_identifier": draggedSegment.blockIdentifier,
                                            "id": originalSegment.element,
                                            "alt": originalSegment.alt
                                        });
                                    });
                                }

                                if (Math.abs(segment.start_time - time) > 500) {
                                    setElementsInvolvedByRole(elementsInvolvedByRole);
                                    setBlockIdentifier(draggedSegment.blockIdentifier);
                                    setTime(time);
                                    // handleDragDropRequest(segment, draggedSegment.blockIdentifier, time);
                                    // TODO this is incorrect: because the segment has not been updated from a generative edit -> resulting in new element offsets
                                    // alert(`Segment ${JSON.stringify(segment.targets)} moved to ${time} seconds from ${segment.start_time} seconds`);
                                }

                                setDragAction(true);
                                // Don't clear selectedSegments after drag - preserve multi-selection
                            }
                        }}
                    >
                        <Segment
                            borderColor={getColorByRole(segment.target_type.toLowerCase(), true)}
                            backgroundColor={isSelected ? getColorByRole(segment.target_type.toLowerCase(), false) : '#ffffff'}
                            className={`timeline-segment segment-container ${expandedTrack === trackType ? 'expanded' : ''}`}
                            style={{
                                left: `calc(${(segment.start_time / totalDuration) * 100}% + 4.17%)`,
                                width: `${((segment.end_time - segment.start_time) / totalDuration) * 100}%`,
                                height: isMobile ? '3.5em' : '4em',
                            }}
                            onClick={(event) => {
                                // Only handle click if not dragging
                                if (!isDragging) {
                                    handleMultipleSegmentClick(segment, trackType);
                                }
                            }}
                            segmentType={segment.animation_event}
                            onFlairAction={(action, segmentType) => handleFlairAction(action, segmentType, segment)}
                            onDelete={() => handleDeleteSegment(segment)}
                            isSelected={isSelected}
                            isExpanded={expandedTrack === trackType}
                        >
                            {expandedTrack === trackType ? (
                                // Expanded view with edit functionality
                                <Box className="segment-edit-container">
                                    {editingSegment === segment.animation_event ? (
                                        // Edit mode
                                        <>
                                            <TextField
                                                value={editText}
                                                onChange={(e) => setEditText(e.target.value)}
                                                variant="outlined"
                                                size="small"
                                                className="segment-edit-field"
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                            <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleSaveEdit(segment);
                                                }}
                                                className="segment-edit-button"
                                            >
                                                <CheckIcon fontSize="small" />
                                            </IconButton>
                                        </>
                                    ) : (
                                        // Display mode
                                        <>
                                            <Typography
                                                variant={isMobile ? "caption" : "body2"}
                                                className={`segment-text ${isSelected ? 'selected' : ''} ${isMobile ? 'mobile' : 'desktop'} expanded`}
                                            >
                                                {segment.animation_event}
                                            </Typography>
                                            <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleEditSegment(segment);
                                                }}
                                                className="segment-edit-button"
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </>
                                    )}
                                </Box>
                            ) : (
                                // Collapsed view (original)
                                <Typography
                                    variant={isMobile ? "caption" : "body2"}
                                    className={`segment-text ${isSelected ? 'selected' : ''} ${isMobile ? 'mobile' : 'desktop'}`}
                                >
                                    {segment.animation_event}
                                </Typography>
                            )}
                        </Segment>
                    </Draggable>
                );

                // Add the segment to the appropriate track based on its groupRole
                if (trackType === 'Primary') {
                    tracks.Primary.push(segmentElement);
                } else if (trackType === 'Secondary') {
                    tracks.Secondary.push(segmentElement);
                } else if (trackType === 'Text') {
                    tracks.Text.push(segmentElement);
                } else if (trackType === 'Background') {
                    tracks.Background.push(segmentElement);
                }
            });
        });
    }


    const [modalOpen, setModalOpen] = useState(false);

    const handleOpenModal = () => setModalOpen(true);
    const handleCloseModal = () => setModalOpen(false);
    return (
        <div ref={timelineRef} className="narrative-timeline" style={{
            height: isMobile ? '50%' : '40%',
        }}>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1}
                className="timeline-header"
            >
                <Box className="timeline-header-box">

                </Box>

                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    className="timeline-toolbar"
                >
                    {/* <Button
                        variant="outlined"
                        startIcon={<ChatBubbleOutlineIcon />}
                        onClick={handleOpenPromptDock}
                        size={isMobile ? "small" : "medium"}
                        sx={tabStyles}
                    >
                        Write Prompt
                    </Button> */}
                    <QuickActions />
                </Stack>

                <Modal
                    open={modalOpen}
                    onClose={handleCloseModal}
                    aria-labelledby="info-modal-title"
                >
                    <Paper className="info-modal">
                        <Box className="info-modal-header">
                            <Typography id="info-modal-title" variant="h6">
                                Edit Timeline Information
                            </Typography>
                            <IconButton size="small" onClick={handleCloseModal}>
                                <CloseIcon />
                            </IconButton>
                        </Box>

                        <Typography component="ul" className="info-modal-list">
                            <Typography component="li" className="info-modal-list-item">
                                View the Timeline of the generated animation.
                            </Typography>
                            <Typography component="li" className="info-modal-list-item">
                                Use Quick Actions to modify the animation.
                            </Typography>
                            <Typography component="li" className="info-modal-list-item">
                                Give prompts to edit or generate new animations.
                            </Typography>
                        </Typography>

                        <Box className="info-modal-footer">
                            <Button onClick={handleCloseModal}>Close</Button>
                        </Box>
                    </Paper>
                </Modal>
            </Stack>

            <div
                className="timeline-container"
                style={{
                    height: isMobile ? 'calc(100% - 120px)' : 'calc(100% - 100px)',
                }}
            >

                <div className="timeline-grid-container">
                    <Grid container spacing={isMobile ? 1 : 2} className="timeline-grid">
                        <Grid item xs={0.8} className="grid-item-placeholder">

                        </Grid>
                        <Grid item xs={11.2} className="grid-item-ruler">
                            <Ruler totalDuration={totalDuration} interval={1000} />
                        </Grid>

                        <Grid item xs={0.7} className="grid-item-track-label">
                            <Typography
                                variant={isMobile ? "caption" : "body2"}
                                className={`track-label ${selectedSegments.some(s => 
                                    animationBlocks?.Primary?.some(segment => 
                                        segment.animation_event === s.animation_event
                                    )
                                ) ? 'has-selected' : ''}`}
                            >
                                Primary
                            </Typography>
                        </Grid>
                        <Grid item xs={11.3} className="grid-item-track">
                            <Box id="primary-track" className={`track ${expandedTrack === 'Primary' ? 'expanded' : ''}`}
                                onClick={() => setExpandedTrack(expandedTrack === 'Primary' ? null : 'Primary')}>
                                {tracks.Primary}
                            </Box>
                        </Grid>

                        <Grid item xs={0.7} className="grid-item-track-label">
                            <Typography
                                variant={isMobile ? "caption" : "body2"}
                                className={`track-label ${selectedSegments.some(s => 
                                    animationBlocks?.Secondary?.some(segment => 
                                        segment.animation_event === s.animation_event
                                    )
                                ) ? 'has-selected' : ''}`}
                            >
                                Secondary
                            </Typography>
                        </Grid>
                        <Grid item xs={11.3} className="grid-item-track">
                            <Box id="secondary-track" className={`track ${expandedTrack === 'Secondary' ? 'expanded' : ''}`}
                                onClick={() => setExpandedTrack(expandedTrack === 'Secondary' ? null : 'Secondary')}>
                                {tracks.Secondary}
                            </Box>
                        </Grid>

                        <Grid item xs={0.7} className="grid-item-track-label">
                            <Typography
                                variant={isMobile ? "caption" : "body2"}
                                className={`track-label ${selectedSegments.some(s => 
                                    animationBlocks?.Text?.some(segment => 
                                        segment.animation_event === s.animation_event
                                    )
                                ) ? 'has-selected' : ''}`}
                            >
                                Text
                            </Typography>
                        </Grid>
                        <Grid item xs={11.3} className="grid-item-track">
                            <Box id="text-track" className={`track ${expandedTrack === 'Text' ? 'expanded' : ''}`}
                                onClick={() => setExpandedTrack(expandedTrack === 'Text' ? null : 'Text')}>
                                {tracks.Text}
                            </Box>
                        </Grid>

                        <Grid item xs={0.7} className="grid-item-track-label">
                            <Typography
                                variant={isMobile ? "caption" : "body2"}
                                className={`track-label ${selectedSegments.some(s => 
                                    animationBlocks?.Background?.some(segment => 
                                        segment.animation_event === s.animation_event
                                    )
                                ) ? 'has-selected' : ''}`}
                            >
                                Background
                            </Typography>
                        </Grid>
                        <Grid item xs={11.3} className="grid-item-track">
                            <Box id="background-track" className={`track ${expandedTrack === 'Background' ? 'expanded' : ''}`}
                                onClick={() => setExpandedTrack(expandedTrack === 'Background' ? null : 'Background')}>
                                {tracks.Background}
                            </Box>
                        </Grid>
                    </Grid>
                </div>
            </div>
        </div>
    );
};

export default NarrativeTimeline;
